import React, { useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { BreadcrumbView } from '../../../../../context/breadcrumb.context'
import { PROVIDER_SECTION_BREADCRUMB } from './provider-breadcrumb'
import { useApiFormProviderExpirySweep, useApiListProviderHealth, useApiListProviderHealthJobs } from '../../../../../api-new/state/auth'
import { IconProviderHealth } from '../../../../../shared/icon/icon'
import { useRoleSysProvider } from '../../../../../role/hook/role-sys'
import { FleetPage, FleetSection, RollupTiles } from '../../../../../shared/sso-workspace/fleet-page'
import { FleetGrid, useGridList } from '../../../../../shared/sso-workspace/fleet-grid'
import { TableAgClient } from '../../../../../shared/table-builder/table-ag-client'
import { ROW_HEIGHT_TWO_LINE } from '../../../../../shared/table-builder/table-ag'
import { ColumnFormat, ColumnMenu, ColumnSelector, ColumnText } from '../../../../../shared/table-builder/table-columns'
import { Menu, MenuDetails } from '../../../../../shared/menu/menu'
import { Pill } from '../../../../../shared/sso-workspace/pill'
import { formatAgo, formatDateTime, formatDuration, plural, serverMessage } from '../../../../../shared/sso-workspace/format'
import { healthVerdict, connectionKind } from './provider-list'
import { CredentialCheckResult, credentialCheckView } from '../../../../../shared/sso-workspace/credential-check'
import { ErrorReference } from '../../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../../shared/error/use-error-line'

const warningsOf = row => (row.discovery && row.discovery.warnings) || []
const errorsOf = row => row.errors || []
const yesNo = (value, yes, no) => (value === true ? yes : value === false ? no : 'Not checked')

// When the credential runs out, in words.
const expiryText = row => {
  if (row.daysToExpiry === null || row.daysToExpiry === undefined) return { text: 'Not tracked' }
  if (row.daysToExpiry <= 0) return { text: 'Expired', tone: 'bad' }
  return { text: `${plural(row.daysToExpiry, 'day')} left`, tone: row.daysToExpiry <= 30 ? 'warn' : null }
}

export const JOB_NAMES = {
  'credential-expiry': 'Credential expiry warnings',
  'domain-reverify': 'Daily domain check',
  'consent-request-expiry': 'Consent request expiry'
}

const JOB_TONE = { succeeded: 'ok', failed: 'bad', running: 'warn', skipped: null, abandoned: null }
const JOB_LABEL = { succeeded: 'Succeeded', failed: 'Failed', running: 'Running', skipped: 'Skipped', abandoned: 'Abandoned' }
export const JobStatusPill = ({ status }) => <Pill tone={JOB_TONE[status] || null}>{JOB_LABEL[status] || status || 'Never run'}</Pill>

// Fleet health (spec 4.1) in the workspace's language (audit F8, section 4):
// each connection by name, linked to its workspace, with one verdict and the
// checks behind it in words instead of a grid of yes/no columns. Details
// open the errors and warnings themselves. The scheduled checks' last runs
// sit below, with the way to the Jobs page.
export const ProviderHealth = () => {
  const apiListProviderHealth = useApiListProviderHealth()
  const apiFormProviderExpirySweep = useApiFormProviderExpirySweep()
  const roleSysProvider = useRoleSysProvider()

  const [rows, setRows] = useState(undefined)
  const [status, setStatus] = useState('')
  const [error, failure, setError] = useErrorLine()
  const [running, setRunning] = useState(false)
  const [openID, setOpenID] = useState(null)

  const load = async () => {
    try {
      setRows((await apiListProviderHealth.get()) || [])
    } catch (err) {
      setRows(null)
    }
  }

  const tableRef = useRef()
  
  const handleRunSweep = async () => {
    setRunning(true)
    setError(null)
    setStatus('')
    try {
      await apiFormProviderExpirySweep.post()
      setStatus('Credential check finished. Anyone due a warning about an expiring or rejected credential has been emailed.')
      await load()
    } catch (err) {
      setError(serverMessage(err, "The credential check couldn't run."), err)
    } finally {
      setRunning(false)
    }
  }

  const counts = useMemo(() => {
    const c = { ok: 0, warn: 0, bad: 0 }
    ;(rows || []).forEach(r => { const v = healthVerdict(r); if (v.tone) c[v.tone] += 1 })
    return c
  }, [rows])

  const figure = n => (rows === undefined ? '…' : rows === null ? 'Unavailable' : String(n))

  // Every figure the grid shows is flattened onto the row as the words it
  // shows, so the grid's quick filter, sort and CSV export work off them.
  const gridRows = useMemo(
    () =>
      Array.isArray(rows)
        ? rows.map(r => {
            const verdict = healthVerdict(r)
            const accepted = credentialCheckView(r.credentialCheck)
            return {
              ...r,
              id: r.providerID,
              kind: connectionKind(r),
              verdict: verdict.label,
              verdictRank: -verdict.rank,
              why: [...errorsOf(r), ...warningsOf(r)].join('; '),
              issuer: yesNo(r.discovery && r.discovery.issuerMatches, 'Matches', "Doesn't match"),
              signin: yesNo(r.authUrlBuilds, 'Builds', 'Fails'),
              accepted: accepted.label,
              expiry: expiryText(r).text,
              expirySort: r.daysToExpiry === null || r.daysToExpiry === undefined ? Infinity : r.daysToExpiry,
              // In the row data (not only getRowClass): AG Grid only restyles
              // a row whose data changed, so opening details must change it.
              open: r.providerID === openID
            }
          })
        : rows,
    [rows, openID]
  )
  const gridList = useGridList(gridRows)

  const opened = (gridRows || []).find(r => r.providerID === openID)

  const detail = r =>
    <div className="d-grid gap-3 py-1">
      {errorsOf(r).length > 0 &&
        <div>
          <h3 className="h6 fw-semibold sw-tone-bad">Problems</h3>
          <ul className="mb-0">{errorsOf(r).map(e => <li key={e}>{e}</li>)}</ul>
        </div>}
      {warningsOf(r).length > 0 &&
        <div>
          <h3 className="h6 fw-semibold sw-tone-warn">Warnings</h3>
          <ul className="mb-0">{warningsOf(r).map(w => <li key={w}>{w}</li>)}</ul>
        </div>}
      {!errorsOf(r).length && !warningsOf(r).length && <p className="sw-hint m-0">No problems or warnings.</p>}
      <CredentialCheckResult check={r.credentialCheck || { status: 'not_checked', summary: "This connection's credential wasn't checked." }} />
      <dl className="sw-dl">
        <dt>Issuer</dt><dd>{yesNo(r.discovery && r.discovery.issuerMatches, 'Matches what the identity provider publishes', "Doesn't match what the identity provider publishes")}</dd>
        <dt>Sign-out endpoint</dt><dd>{r.discovery && r.discovery.endSessionEndpoint ? <code>{r.discovery.endSessionEndpoint}</code> : 'None published'}</dd>
        <dt>Sign-out in a frame</dt><dd>{yesNo(r.discovery && r.discovery.logoutFrameBlocked, 'Blocked by the identity provider', 'Allowed')}</dd>
        <dt>Client authentication</dt><dd>{((r.discovery && r.discovery.authMethods) || []).join(', ') || 'Not published'}</dd>
        <dt>Email domains</dt><dd>{yesNo(r.domainsResolve, 'All resolve', "Some don't resolve")}</dd>
        <dt>Sign-in link</dt><dd>{yesNo(r.authUrlBuilds, 'Builds', "Couldn't be built")}</dd>
      </dl>
      <div><Link className="btn btn-sm sw-btn" to={`/admin/provider/list/edit/${r.providerID}`}>Open {r.providerName}</Link></div>
    </div>

  return (
    <BreadcrumbView parent={PROVIDER_SECTION_BREADCRUMB} Icon={IconProviderHealth} text="HEALTH">
      <FleetPage
        tabs
        title="Health"
        purpose="Whether each connection's identity provider answers the way sign-in needs it to, accepts its credential, and how long that credential has left."
        actions={roleSysProvider.accessUpdate &&
          <button type="button" className="btn sw-btn sw-btn-primary" disabled={running} onClick={handleRunSweep}>
            {running ? 'Checking credentials…' : 'Check credentials now'}
          </button>}>
        <RollupTiles label="health at a glance" tiles={[
          { key: 'ok', label: 'Healthy', value: figure(counts.ok), tone: null },
          { key: 'warn', label: 'With warnings', value: figure(counts.warn), tone: counts.warn ? 'warn' : null },
          { key: 'bad', label: 'With problems', value: figure(counts.bad), tone: counts.bad ? 'bad' : null }
        ]} />
        {error && <p className="text-danger small" role="alert">{error}</p>}
        {error && <ErrorReference error={failure} />}

        {status && <p className="sw-status" role="status" aria-live="polite">{status}</p>}
        {rows === null && <p className="text-danger small" role="alert">The connection health couldn't be loaded. Reload to try again.</p>}

        <FleetGrid title="connection health" tableRef={tableRef} exportName="connection-health">
          <TableAgClient
            ref={tableRef}
            apiList={gridList}
            onReady={load}
            sortCol="verdict"
            rowHeight={ROW_HEIGHT_TWO_LINE}
            getRowClass={params => (params.data && params.data.open ? 'sw-hl' : '')}
            emptyState={{ Icon: IconProviderHealth, title: 'No connections to check', message: 'No connections to check yet. Health appears here once a connection exists.' }}>
            <ColumnFormat field="providerName" header="Connection" render={connectionCell} stacked minWidth={200} flex={2} sortable />
            <ColumnSelector field="verdict" header="Health" selector={r => <Pill tone={healthVerdict(r).tone}>{r.verdict}</Pill>} comparator={byRank} sortable />
            <ColumnFormat field="why" header="What we found" render={whyCell} stacked minWidth={200} flex={2} sortable />
            <ColumnText field="issuer" header="Issuer" sortable />
            <ColumnText field="signin" header="Sign-in link" sortable />
            <ColumnSelector field="accepted" header="Credential accepted"
              selector={r => <Pill tone={credentialCheckView(r.credentialCheck).tone} title={r.credentialCheck && r.credentialCheck.summary}>{r.accepted}</Pill>} sortable />
            <ColumnSelector field="expiry" header="Credential" selector={expiryCell} comparator={byExpiry} sortable />
            <ColumnMenu
              menu={r =>
                <Menu small>
                  <MenuDetails text={r.open ? 'hide details' : 'details'} show onClick={() => setOpenID(id => (id === r.providerID ? null : r.providerID))} />
                </Menu>}
            />
          </TableAgClient>
        </FleetGrid>

        {opened &&
          <FleetSection id="health-detail" title={`Details: ${opened.providerName}`}>
            <div data-testid={`detail-${opened.providerID}`}>{detail(opened)}</div>
          </FleetSection>}

        <FleetSection id="scheduled" title="Scheduled checks" lede="The jobs that watch domains and credentials every day."
          actions={<Link className="btn btn-sm sw-btn" to="/admin/provider/jobs">All jobs</Link>}>
          <ScheduledChecks />
        </FleetSection>
      </FleetPage>
    </BreadcrumbView>
  )
}

const connectionCell = row =>
  row.data
    ? <span>
        <Link to={`/admin/provider/list/edit/${row.data.providerID}`} className="fw-semibold">{row.data.providerName}</Link>
        <span className="sw-cell-sub">{row.data.kind}</span>
      </span>
    : ''

const whyCell = row => {
  if (!row.data) return ''
  const r = row.data
  const first = errorsOf(r)[0] || warningsOf(r)[0]
  const more = errorsOf(r).length + warningsOf(r).length - 1
  return first ? <span>{first}{more > 0 && <span className="sw-cell-sub">and {plural(more, 'more issue')}</span>}</span> : <span className="sw-hint">Nothing to report</span>
}

const expiryCell = r => {
  const e = expiryText(r)
  return <span className={e.tone ? `sw-tone-${e.tone} fw-semibold` : e.text === 'Not tracked' ? 'sw-hint' : undefined}>{e.text}</span>
}

// Worst first when ascending: a problem, then warnings, then healthy, then
// not checked.
const byRank = (a, b, nodeA, nodeB) => (nodeA.data.verdictRank || 0) - (nodeB.data.verdictRank || 0)

const byExpiry = (a, b, nodeA, nodeB) => {
  const x = nodeA.data.expirySort
  const y = nodeB.data.expirySort
  return x === y ? 0 : x < y ? -1 : 1
}

const jobStatusCell = row => (row.data ? <JobStatusPill status={row.data.status} /> : '')
const jobAgoCell = row => (row.value ? <span title={formatDateTime(row.value)}>{formatAgo(row.value)}</span> : '')
const jobDurationCell = row => formatDuration(row.value)
const jobErrorCell = row => (row.value ? <span className="sw-tone-bad">{row.value}</span> : '')

// The latest run of each scheduled job (GET /provider-health/jobs).
const ScheduledChecks = () => {
  const api = useApiListProviderHealthJobs()
  const tableRef = useRef()
  const [jobs, setJobs] = useState(undefined)

  const load = async () => {
    try { setJobs((await api.get()) || []) } catch (err) { setJobs(null) }
  }

  const rows = useMemo(() => (Array.isArray(jobs) ? jobs.map(j => ({ ...j, id: j.name, job: JOB_NAMES[j.name] || j.name })) : jobs), [jobs])
  const gridList = useGridList(rows)

  return (
    <FleetGrid compact flush title="latest runs" tableRef={tableRef} exportName="scheduled-checks">
      {/* queryKey: the page's other grid (connection health) keeps the plain
          sort/filter keys; without one they overwrote each other's sort. */}
      <TableAgClient
        ref={tableRef}
        queryKey="runs"
        apiList={gridList}
        onReady={load}
        emptyState={{ Icon: IconProviderHealth, title: 'No runs yet', message: 'No scheduled check has run yet.' }}>
        <ColumnText field="job" header="Job" sortable />
        <ColumnFormat field="status" header="Last run" render={jobStatusCell} sortable />
        <ColumnFormat field="startedAt" header="Started" render={jobAgoCell} sortable />
        <ColumnFormat field="durationMs" header="Took" render={jobDurationCell} sortable />
        <ColumnFormat field="error" header="Error" render={jobErrorCell} sortable />
      </TableAgClient>
    </FleetGrid>
  )
}
